/**
 * MultiRecipeSelector Component
 * A multi-select recipe picker with pills UI, optional toggle, and search
 *
 * Each pill carries { menuRecipeId?, recipeId, name, category, isOptional }
 * to preserve existing link IDs on edit (stable IDs for future opt-out feature)
 */

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { fetchRecipes } from '@/lib/api/recipes';
import { RECIPE_CATEGORY_LABELS } from '@/types/api';
import { X } from 'lucide-react';
import type { Recipe, MenuRecipeLink } from '@/types/api';

const MAX_RECIPES = 5;
const RECIPE_TOOLTIP_DELAY_MS = 100;

/**
 * SelectedRecipe extends MenuRecipeLink but makes menuRecipeId optional
 * (new links that haven't been persisted yet don't have an ID)
 */
export type SelectedRecipe = Omit<MenuRecipeLink, 'menuRecipeId'> & { menuRecipeId?: number };

interface MultiRecipeSelectorProps {
  value: SelectedRecipe[];
  onChange: (recipes: SelectedRecipe[]) => void;
  disabled?: boolean;
}

export function MultiRecipeSelector({
  value,
  onChange,
  disabled = false,
}: MultiRecipeSelectorProps) {
  const [openRecipeTooltipId, setOpenRecipeTooltipId] = useState<number | null>(null);

  // Stable key that only changes when the set of selected recipe IDs changes
  // (not when isOptional toggles), preventing spurious search refetches
  const selectedIdKey = useMemo(
    () => value.map((r) => r.recipeId).join(','),
    [value]
  );

  const handleSearch = useCallback(async (query: string) => {
    const currentIds = new Set(value.map((r) => r.recipeId));
    const response = await fetchRecipes({
      search: query,
      pageSize: 10,
      activeOnly: true,
    });
    // Filter out already-selected recipes
    return response.recipes.filter((r) => !currentIds.has(r.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdKey]);

  const handleSelect = useCallback((recipe: Recipe) => {
    if (value.length >= MAX_RECIPES) return;
    const newRecipe: SelectedRecipe = {
      recipeId: recipe.id,
      name: recipe.name,
      category: recipe.category,
      isOptional: false,
    };
    onChange([...value, newRecipe]);
  }, [value, onChange]);

  const handleRemove = useCallback((recipeId: number) => {
    setOpenRecipeTooltipId(null);
    onChange(value.filter((r) => r.recipeId !== recipeId));
  }, [value, onChange]);

  const handleToggleOptional = useCallback((recipeId: number) => {
    setOpenRecipeTooltipId(null);
    onChange(
      value.map((r) =>
        r.recipeId === recipeId ? { ...r, isOptional: !r.isOptional } : r
      )
    );
  }, [value, onChange]);

  return (
    <div className="space-y-2">
      {/* Pills for selected recipes */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((recipe) => (
            <div
              key={recipe.recipeId}
              className="flex max-w-full items-center gap-1.5 rounded-full border bg-gray-50 pl-3 pr-1 py-1 text-sm"
            >
              <Tooltip
                delayDuration={RECIPE_TOOLTIP_DELAY_MS}
                open={openRecipeTooltipId === recipe.recipeId}
                onOpenChange={(open) => setOpenRecipeTooltipId(open ? recipe.recipeId : null)}
              >
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="min-w-0 max-w-[32vw] truncate text-left font-medium sm:max-w-[260px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    onClick={() => setOpenRecipeTooltipId(recipe.recipeId)}
                    aria-label={`Show full recipe name for ${recipe.name}`}
                  >
                    {recipe.name}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[min(20rem,calc(100vw-2rem))] whitespace-normal break-words text-center">
                  {recipe.name}
                </TooltipContent>
              </Tooltip>
              <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                {RECIPE_CATEGORY_LABELS[recipe.category]}
              </Badge>
              <button
                type="button"
                onClick={() => handleToggleOptional(recipe.recipeId)}
                disabled={disabled}
                className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${
                  recipe.isOptional
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                }`}
                title={recipe.isOptional ? 'Click to make required' : 'Click to make optional'}
              >
                {recipe.isOptional ? 'optional' : 'required'}
              </button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(recipe.recipeId)}
                disabled={disabled}
                className="shrink-0 h-5 w-5 p-0 rounded-full hover:bg-gray-200"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Search dropdown - hidden at max */}
      {value.length < MAX_RECIPES && (
        <SearchableDropdown<Recipe>
          placeholder="Search recipes to add..."
          onSearch={handleSearch}
          onSelect={handleSelect}
          renderItem={(recipe) => (
            <>
              <span>{recipe.name}</span>
              <span className="text-xs text-gray-500">
                {RECIPE_CATEGORY_LABELS[recipe.category]}
              </span>
            </>
          )}
          getItemKey={(recipe) => recipe.id}
          disabled={disabled}
          emptyMessage="No matching recipes"
        />
      )}

      {value.length >= MAX_RECIPES && (
        <p className="text-xs text-muted-foreground">Maximum {MAX_RECIPES} recipes reached</p>
      )}
    </div>
  );
}
